import { BadgeCheck, GraduationCap, Music2, Sparkles } from "lucide-react";
import { TESTIDS } from "@/lib/site";
import { useReveal } from "@/lib/useReveal";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_compassionate-carson-7/artifacts/aq985br7_36a2ff72-701a-43e5-bf7f-a7fdd64bd297.jfif";

const INTERESTS = [
  "Ansiedad",
  "Depresión",
  "Identidad",
  "Neurodivergencia",
  "Bienestar emocional",
  "Acompañamiento humano",
  "Crecimiento personal",
  "Transiciones de vida",
];

export default function About() {
  const r1 = useReveal();
  const r2 = useReveal(0.1);

  return (
    <section
      id="sobre-mi"
      data-testid={TESTIDS.about.section}
      className="relative py-24 md:py-32 bg-warm-beige/40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Imagery */}
        <div className={`${r1.className} lg:col-span-5 lg:sticky lg:top-32`}>
          <div className="relative max-w-[380px] mx-auto lg:mx-0">
            <div className="aspect-[3/4] rounded-[2rem] overflow-hidden shadow-xl shadow-editorial-dark/10 border border-warm-beige">
              <img src={PORTRAIT} alt="Retrato editorial — Berenice" className="w-full h-full object-cover object-[center_30%]" loading="lazy" />
            </div>
            {/* Floating credential chip */}
            <div
              data-testid={TESTIDS.about.licenseBadge}
              className="absolute -bottom-8 -right-4 md:right-6 bg-warm-white rounded-2xl shadow-lg shadow-editorial-dark/10 border border-dusty-rose/30 px-5 py-4 max-w-[220px]"
            >
              <div className="flex items-center gap-2 text-accent-magenta">
                <BadgeCheck size={18} />
                <span className="text-[0.7rem] tracking-[0.18em] uppercase">Cédula</span>
              </div>
              <p className="font-serif text-[1.6rem] text-editorial-dark leading-none mt-1">
                15510639
              </p>
              <p className="text-xs text-editorial-gray mt-1">Psicóloga titulada</p>
            </div>
          </div>
        </div>

        {/* Text + bento */}
        <div className={`${r2.className} lg:col-span-7`}>
          <p className="text-[0.78rem] tracking-[0.22em] uppercase text-accent-magenta mb-4">
            Sobre mí
          </p>
          <h2
            data-testid={TESTIDS.about.title}
            className="font-serif text-4xl md:text-5xl lg:text-[3.4rem] text-editorial-dark leading-[1.05]"
          >
            Acompaño procesos
            <span className="italic text-terracotta"> emocionales </span>
            con sensibilidad y profundidad.
          </h2>
          <div className="mt-7 space-y-5 text-editorial-gray text-[1.02rem] leading-relaxed max-w-2xl">
            <p>
              Mi camino comenzó en la música, con una formación profunda en el saxofón jazz, y
              floreció en la psicología y la musicoterapia: dos lenguajes que se entrelazan
              para escuchar lo humano desde un lugar honesto y cercano.
            </p>
            <p>
              Creo en una terapia que no busca arreglar, sino acompañar. Un espacio cálido,
              transparente y seguro donde cada emoción —incluso la más incómoda— pueda ser
              recibida con respeto.
            </p>
          </div>

          {/* Bento credentials */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <Credential
              icon={<BadgeCheck size={20} />}
              title="Psicóloga titulada"
              detail="Cédula Profesional 15510639"
              tone="rose"
            />
            <Credential
              icon={<GraduationCap size={20} />}
              title="Maestría IMMH"
              detail="Musicoterapia Humanista"
              tone="sage"
            />
            <Credential
              icon={<Music2 size={20} />}
              title="Saxofón Jazz"
              detail="Conservatorio de Música del Estado de México"
              tone="lavender"
              wide
            />
          </div>

          {/* Interests */}
          <div className="mt-10">
            <p className="flex items-center gap-2 text-sm tracking-widest uppercase text-editorial-gray mb-4">
              <Sparkles size={14} className="text-accent-magenta" />
              Áreas de interés
            </p>
            <div className="flex flex-wrap gap-2.5">
              {INTERESTS.map((label) => (
                <span
                  key={label}
                  className="rounded-full bg-warm-white border border-dusty-rose/30 px-4 py-1.5 text-sm text-editorial-dark hover:border-accent-magenta/40 hover:text-accent-magenta transition-colors"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Credential({ icon, title, detail, tone, wide }) {
  const toneMap = {
    rose: "bg-dusty-rose/30 text-accent-magenta",
    sage: "bg-sage/30 text-[#3F5340]",
    lavender: "bg-lavender/40 text-accent-magenta",
  };
  return (
    <div
      className={`rounded-2xl bg-warm-white border border-dusty-rose/20 p-5 hover:-translate-y-0.5 transition-transform ${
        wide ? "sm:col-span-2" : ""
      }`}
    >
      <div className={`inline-flex w-10 h-10 items-center justify-center rounded-full ${toneMap[tone]}`}>
        {icon}
      </div>
      <p className="mt-3 font-serif text-[1.35rem] text-editorial-dark leading-tight">
        {title}
      </p>
      <p className="text-sm text-editorial-gray mt-1">{detail}</p>
    </div>
  );
}
